<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />


   <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Click <b>Download Import Template</b> for the right columns, or use a file from <b>Export to Excel</b>: it has the same columns.</li>
                     <li>Every value is required and must be a <b>whole number of at least 60</b>. JWT expiration and Logout after inactivity should be the same.</li>
                     <li>Only one setting is active per company: the <b>last row</b> of the file becomes the active setting, and all others (old and new) become inactive.</li>
                     <li>If no row is valid, nothing is saved and your current settings stay as they are.</li>
                     <li>Settings are imported into the company and branch you are logged into. Switch to a specific branch first.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

         <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->


   
   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update Security Settings' : 'Add Security Settings'"
      title-icon="bi bi-shield-lock"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      @save="saveOrEditRecord"
      @sample="addSampleData"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Row 1: JWT & MFA SMS -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.jwt_expiration_seconds"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="Login expires after (seconds)"
            icon="fa fa-key"
            placeholder="e.g. 3600"
            :hint="durationHint(formInputs.jwt_expiration_seconds, 'Ends the login (clears the JWT)')"
            tooltip="Seconds until the login ends, e.g. 3600 = 1 hour"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <FormField
            v-model="formInputs.mfa_sms_expiration_sec"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="SMS login code expires after (seconds)"
            icon="fa fa-mobile"
            placeholder="e.g. 300"
            :hint="durationHint(formInputs.mfa_sms_expiration_sec)"
            tooltip="Seconds the SMS code works, e.g. 300 = 5 minutes"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Row 2: MFA Email & Email verification -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.mfa_email_expiration_sec"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="Email login code expires after (seconds)"
            icon="fa fa-envelope"
            placeholder="e.g. 600"
            :hint="durationHint(formInputs.mfa_email_expiration_sec)"
            tooltip="Seconds the email code works, e.g. 600 = 10 minutes"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <FormField
            v-model="formInputs.email_verification_expiration_sec"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="Email verification link expires after (seconds)"
            icon="fa fa-check-circle"
            placeholder="e.g. 86400"
            :hint="durationHint(formInputs.email_verification_expiration_sec)"
            tooltip="Seconds the sign-up verification link works, e.g. 86400 = 1 day"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Row 3: Forgot password link & Rate limit -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.forgotpasswordlink_expiration_sec"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="Password reset link expires after (seconds)"
            icon="fa fa-link"
            placeholder="e.g. 3600"
            :hint="durationHint(formInputs.forgotpasswordlink_expiration_sec)"
            tooltip="Seconds the Forgot password link works, e.g. 3600 = 1 hour"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
         <FormField
            v-model="formInputs.ratelimited_expiration_sec"
            col="col-md-6"
            type="number"
            min="1"
            step="1"
            label="Lock after too many logins (minutes)"
            icon="fa fa-ban"
            placeholder="e.g. 61"
            hint="Minutes a user waits after too many wrong logins"
            tooltip="Minutes to wait after too many wrong logins"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>

      <!-- Row 4: Logout after inactivity -->
      <div class="row g-3 mb-0">
         <FormField
            v-model="formInputs.inactivity_timeout_seconds"
            col="col-md-12"
            type="number"
            min="1"
            step="1"
            label="Log out after no activity for (seconds)"
            icon="fa fa-power-off"
            placeholder="e.g. 3600"
            :hint="durationHint(formInputs.inactivity_timeout_seconds, 'Keep it the same as the login expiry')"
            tooltip="Seconds without any activity before logging out, e.g. 3600 = 1 hour"
            required
            @input="handleInputChange"
            @change="handleInputChange"
         />
      </div>
   </RecordFormModal>   


   <h4 class="sa-core-color fw-semibold mb-1">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <p class="small sa-core-color opacity-75 mb-2">
      <i class="fa fa-info-circle me-1"></i>JWT Expiration time and Logout after inactivity should be the same.
   </p>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
         
      <!-- Table action row (shared: TableToolbar.vue) -->
         <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('security_settings.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                     <i class="fa fa-upload fa-fw me-1"></i> Import
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->


         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
          <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('jwt_expiration_seconds')" column="jwt_expiration_seconds" :table-data="tableData" @sort="orderBy">JWT Expiry</SortableTh>
         <SortableTh v-if="isColumnVisible('inactivity_timeout_seconds')" column="inactivity_timeout_seconds" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Inactivity Logout</SortableTh>
         <SortableTh v-if="isColumnVisible('mfa_sms_expiration_sec')" column="mfa_sms_expiration_sec" :table-data="tableData" @sort="orderBy" class="hide-sm-td">MFA SMS</SortableTh>
         <SortableTh v-if="isColumnVisible('mfa_email_expiration_sec')" column="mfa_email_expiration_sec" :table-data="tableData" @sort="orderBy" class="hide-sm-td">MFA Email</SortableTh>
         <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('jwt_expiration_seconds')" :title="record.jwt_expiration_seconds + ' seconds'">
               <!-- Phones: + / − and the number go in front of the value (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ formatDuration(record.jwt_expiration_seconds) }}
            </td>
            <td v-if="isColumnVisible('inactivity_timeout_seconds')" class="hide-sm-td" :title="record.inactivity_timeout_seconds + ' seconds'">{{ formatDuration(record.inactivity_timeout_seconds) }}</td>
            <td v-if="isColumnVisible('mfa_sms_expiration_sec')" class="hide-sm-td" :title="record.mfa_sms_expiration_sec + ' seconds'">{{ formatDuration(record.mfa_sms_expiration_sec) }}</td>
            <td v-if="isColumnVisible('mfa_email_expiration_sec')" class="hide-sm-td" :title="record.mfa_email_expiration_sec + ' seconds'">{{ formatDuration(record.mfa_email_expiration_sec) }}</td>
            <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
               <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
               </span>
            </td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('security_settings.delete')"
                  :can-restore="$hasPermission('security_settings.delete')"
                  :can-delete-permanently="$hasPermission('security_settings.delete')"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                   :modal-id="'deleteCrudModal' + record.id"
                   :title="deleteModalTitle"
                   :message="deleteModalMessage"
                   @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="7" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Setting Number:</strong> {{ record.security_setting_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Creator Name:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-building"></i><strong>Creator Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Creator Branch:</strong> {{ record.branch_name }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('inactivity_timeout_seconds') }"><i class="fa fa-power-off"></i><strong>Inactivity Logout:</strong> {{ formatDuration(record.inactivity_timeout_seconds) }} ({{ record.inactivity_timeout_seconds }}s)</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('mfa_sms_expiration_sec') }"><i class="fa fa-mobile"></i><strong>MFA SMS Expiry:</strong> {{ formatDuration(record.mfa_sms_expiration_sec) }} ({{ record.mfa_sms_expiration_sec }}s)</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('mfa_email_expiration_sec') }"><i class="fa fa-envelope"></i><strong>MFA Email Expiry:</strong> {{ formatDuration(record.mfa_email_expiration_sec) }} ({{ record.mfa_email_expiration_sec }}s)</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                        <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                        <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                        </span>
                     </div>

                     <!-- Always in details -->
                     <div><i class="fa fa-key"></i><strong>JWT Expiry (clears JWT):</strong> {{ formatDuration(record.jwt_expiration_seconds) }} ({{ record.jwt_expiration_seconds }}s)</div>
                     <div><i class="fa fa-check-circle"></i><strong>Email Verification Expiry:</strong> {{ formatDuration(record.email_verification_expiration_sec) }} ({{ record.email_verification_expiration_sec }}s)</div>
                     <div><i class="fa fa-link"></i><strong>Forgot Password Link Expiry:</strong> {{ formatDuration(record.forgotpasswordlink_expiration_sec) }} ({{ record.forgotpasswordlink_expiration_sec }}s)</div>
                     <div><i class="fa fa-ban"></i><strong>Rate Limit:</strong> {{ record.ratelimited_expiration_sec }} minutes</div>
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>

                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ this.$ConvertToLocalDate(record.date_created) }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ this.$ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="9" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No security settings yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Add one to set login and code expiry times.'"
               icon="fa fa-lock"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="9">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>

         </div>
         <!-- Bottom Controls: Delete & Pagination -->
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import securitySettingsMixin from "./mixins/securitySettingsMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'SecuritySettings',
   mixins: [tableMixin, securitySettingsMixin], // shared table methods first, then SecuritySettings' own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/settings/api_security_settings.php", // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "security_settings_import_template.xlsx",
         exportFileName: "securitySettingsExport", // name of the exported file
         columnsKey: "securitysettings",   // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         pageIcon: 'fa fa-lock', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "JWT Expiration (sec)", value: "jwt_expiration_seconds" },
            { label: "Inactivity Timeout (sec)", value: "inactivity_timeout_seconds" },
            { label: "MFA SMS Expiration (sec)", value: "mfa_sms_expiration_sec" },
            { label: "MFA Email Expiration (sec)", value: "mfa_email_expiration_sec" },
            { label: "Email Verification Expiration (sec)", value: "email_verification_expiration_sec" },
            { label: "Forgot Password Link Expiration (sec)", value: "forgotpasswordlink_expiration_sec" },
            { label: "Rate Limit (min)", value: "ratelimited_expiration_sec" },
            { label: "Setting Number", value: "security_setting_number" },
         ],
      };
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );

      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
      // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Security Settings",
              byme: "Security Settings By Me",
              deletedone: "Security Settings Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Security Settings";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "jwt_expiration_seconds", label: "JWT Expiry" },
              { key: "inactivity_timeout_seconds", label: "Inactivity Logout" },
              { key: "mfa_sms_expiration_sec", label: "MFA SMS" },
              { key: "mfa_email_expiration_sec", label: "MFA Email" },
              { key: "is_active", label: "Status" },
          ];
      },
   },
   methods: {
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            jwt_expiration_seconds: "",
            mfa_sms_expiration_sec: "",
            mfa_email_expiration_sec: "",
            email_verification_expiration_sec: "",
            forgotpasswordlink_expiration_sec: "",
            ratelimited_expiration_sec: "",
            inactivity_timeout_seconds: ""
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'jwt_expiration_seconds',
            searchPlaceHolderText: 'Search by JWT Expiration (sec)',
            currentSortColumn: 'is_active', // active setting first
         };
      },
      
      // Note under a seconds box: "= 1 hr · Ends the login" (just the note while it's empty)
      durationHint(seconds, note = "") {
         const filled = seconds !== null && seconds !== undefined && seconds !== "" && !isNaN(Number(seconds));
         const time = filled ? "= " + this.formatDuration(seconds) : "";
         return [time, note].filter(Boolean).join(" · ");
      },
      // Turn seconds into a readable duration: 3600 -> "1 hr", 300 -> "5 min"
      formatDuration(seconds) {
         const s = Number(seconds);
         if (seconds === null || seconds === undefined || seconds === "" || isNaN(s)) return "-";
         if (s >= 86400 && s % 86400 === 0) return `${s / 86400} day${s / 86400 > 1 ? "s" : ""}`;
         if (s >= 3600 && s % 3600 === 0) return `${s / 3600} hr`;
         if (s >= 60 && s % 60 === 0) return `${s / 60} min`;
         return `${s} sec`;
      },
      addSampleData() {
         this.formInputs = {
            ...this.getDefaultFormInputs(),
            jwt_expiration_seconds: "3600",             // 1 hour
            mfa_sms_expiration_sec: "300",              // 5 minutes
            mfa_email_expiration_sec: "600",            // 10 minutes
            email_verification_expiration_sec: "86400", // 24 hours
            forgotpasswordlink_expiration_sec: "3600",  // 1 hour
            ratelimited_expiration_sec: "61",           // labelled minutes; must be > 60 to pass your check
            inactivity_timeout_seconds: "3600",         // same as JWT
         };

         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      saveOrEditRecord() {    
      if (this.isSaving) return;
      this.isSaving = true;

      try {     
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;

         const formIsValid = this.$validateForm('#recordForm');
          if (!formIsValid) { 
            this.$playErrorSound();
                this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                this.isSaving = false;
                return;
          }

          // Make sure all security settings are more than 60
          const securityFields = [
             {
                field: 'jwt_expiration_seconds',
                label: 'JWT Expiration Time'
             },
             {
                field: 'mfa_sms_expiration_sec',
                label: 'MFA SMS Expiration Time'
             },
             {
                field: 'mfa_email_expiration_sec',
                label: 'MFA Email Expiration Time'
             },
             {
                field: 'email_verification_expiration_sec',
                label: 'Email Verification Expiration Time'
             },
             {
                field: 'forgotpasswordlink_expiration_sec',
                label: 'Forgot Password Link Expiration Time'
             },
             {
                field: 'ratelimited_expiration_sec',
                label: 'Rate Limited Expiration Time'
             },
             {
                field: 'inactivity_timeout_seconds',
                label: 'Inactivity Timeout'
             }
          ];

          for (const item of securityFields) {
             const value = Number(this.formInputs[item.field]);

             if (isNaN(value) || value <= 60) {
                this.handleAlert(
                   "ending",
                   `${item.label} must be more than 60 seconds.`,
                   "danger",
                   "modal"
                );
                this.isSaving = false;   // add this
                return;
             }
          }

          this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
              this.tableData.currentPage = 1;
         }

         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   this.handleError("starting",response.data.message[0],"danger","modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
            .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("jwt_expiration_seconds", this.formInputs.jwt_expiration_seconds);
         formData.append("mfa_sms_expiration_sec", this.formInputs.mfa_sms_expiration_sec);
         formData.append("mfa_email_expiration_sec", this.formInputs.mfa_email_expiration_sec);
         formData.append("email_verification_expiration_sec", this.formInputs.email_verification_expiration_sec);
         formData.append("forgotpasswordlink_expiration_sec", this.formInputs.forgotpasswordlink_expiration_sec);
         formData.append("ratelimited_expiration_sec", this.formInputs.ratelimited_expiration_sec);
         formData.append("inactivity_timeout_seconds", this.formInputs.inactivity_timeout_seconds);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.formInputs.id = records.id ?? '';
         this.formInputs.jwt_expiration_seconds = records.jwt_expiration_seconds ?? '';
         this.formInputs.mfa_sms_expiration_sec = records.mfa_sms_expiration_sec ?? '';
         this.formInputs.mfa_email_expiration_sec = records.mfa_email_expiration_sec ?? '';
         this.formInputs.email_verification_expiration_sec = records.email_verification_expiration_sec ?? '';
         this.formInputs.forgotpasswordlink_expiration_sec = records.forgotpasswordlink_expiration_sec ?? '';
         this.formInputs.ratelimited_expiration_sec = records.ratelimited_expiration_sec ?? '';
         this.formInputs.inactivity_timeout_seconds = records.inactivity_timeout_seconds ?? '';
         this.$clearValidationStates('#recordForm');
      },
         },
}; 
</script>